import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fieldCls = "w-full bg-[#0B132B] border border-[#1E293B] rounded-md px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#F97316] transition-colors";
const empty = { name: "", role: "", text: "", rating: 5, published: true };

export default function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const r = await api.get("/admin/testimonials");
      setItems(r.data);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const open = (t) => {
    setEditing(t ? t.id : "new");
    setForm(t ? { name: t.name, role: t.role || "", text: t.text, rating: t.rating, published: t.published } : empty);
  };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/admin/testimonials", form);
      else await api.put(`/admin/testimonials/${editing}`, form);
      toast.success("Testimonial saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-6" data-testid="testimonials-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold text-white">Testimonials ({items.length})</h2>
        <button onClick={() => open(null)} data-testid="add-testimonial-btn"
          className="flex items-center gap-1.5 text-xs bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold rounded-md px-3 py-2 transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Testimonial
        </button>
      </div>

      {editing && (
        <div className="bg-[#131C38] border border-[#F97316]/40 rounded-lg p-5 mb-6" data-testid="testimonial-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">{editing === "new" ? "New Testimonial" : "Edit Testimonial"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Client name" className={fieldCls} data-testid="testimonial-name-input" />
            <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="Role (e.g. Owner-Operator)" className={fieldCls} data-testid="testimonial-role-input" />
          </div>
          <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
            placeholder="Testimonial text" rows={3} className={`${fieldCls} mt-3`} data-testid="testimonial-text-input" />
          <div className="flex items-center gap-6 mt-3">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              Rating
              <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                className="bg-[#0B132B] border border-[#1E293B] rounded px-2 py-1.5 text-xs" data-testid="testimonial-rating-select">
                {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} stars</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={form.published} data-testid="testimonial-published-toggle"
                onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#F97316] w-4 h-4" /> Published
            </label>
          </div>
          <button onClick={save} data-testid="testimonial-save-btn"
            className="mt-4 bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold text-xs px-5 py-2.5 rounded-md transition-colors">Save</button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-[#131C38] border border-[#1E293B] rounded-lg p-5" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-white">{t.name} <span className="text-slate-500 font-normal text-xs">· {t.role}</span></span>
              <div className="flex gap-1.5">
                <button onClick={() => open(t)} data-testid={`edit-testimonial-${t.id}`} className="p-1.5 text-slate-400 hover:text-[#F97316]"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(t.id)} data-testid={`delete-testimonial-${t.id}`} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="flex gap-0.5 mb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-3 h-3 ${i < t.rating ? "text-[#F97316] fill-[#F97316]" : "text-slate-600"}`} />
              ))}
            </div>
            <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{t.text}</p>
            {!t.published && <span className="text-[10px] font-mono2 text-slate-500 uppercase mt-2 inline-block">Hidden</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
